<template>
  <div>
    <a-page-header title="配置管理" />

    <a-card style="margin-top: 16px">
      <a-tabs v-model:activeKey="activeTab">
        <a-tab-pane key="providers" tab="服务商管理">
          <ProviderManage />
        </a-tab-pane>


        <a-tab-pane key="models" tab="模型管理">
          <div style="margin-bottom: 16px">
            <a-button type="primary" @click="handleImportFromOpenRouter" style="margin-right: 8px">
              <template #icon><DownloadOutlined /></template>
              从 OpenRouter 导入
            </a-button>
            <a-button @click="handleAddModel">
              <template #icon><PlusOutlined /></template>
              手工添加
            </a-button>
          </div>

          <a-table
            :columns="modelColumns"
            :data-source="modelList"
            :loading="modelLoading"
            :pagination="modelPagination"
            row-key="id"
            @change="handleTableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'source'">
                <a-tag :color="getSourceColor(record.source)">
                  {{ getSourceName(record.source) }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'is_default'">
                <a-checkbox
                  :checked="record.is_default === 1"
                  @change="(e) => handleToggleDefault(record, e.target.checked)"
                />
              </template>
              <template v-else-if="column.key === 'is_backend'">
                <a-checkbox
                  :checked="record.is_backend === 1"
                  @change="(e) => handleToggleBackend(record, e.target.checked)"
                />
              </template>
              <template v-else-if="column.key === 'is_semantic_match'">
                <a-radio
                  :checked="record.is_semantic_match === 1"
                  @change="() => handleToggleSemanticMatch(record, true)"
                />
              </template>
              <template v-else-if="column.key === 'priority'">
                <a-input-number
                  v-if="record.is_default === 1"
                  v-model:value="record.priority"
                  :min="0"
                  :max="999"
                  size="small"
                  style="width: 80px"
                  @change="() => handleUpdatePriority(record)"
                />
                <span v-else>-</span>
              </template>
              <template v-else-if="column.key === 'is_enabled'">
                <a-switch
                  :checked="record.is_enabled === 1"
                  @change="(checked) => handleToggleEnabled(record, checked)"
                />
              </template>
              <template v-else-if="column.key === 'is_tested'">
                <a-tag :color="record.is_tested === 1 ? 'green' : 'default'">
                  {{ record.is_tested === 1 ? '已测试' : '未测试' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'alias'">
                <span v-if="record.alias" >{{ record.alias }}</span>
                <span v-else style="color: #bbb">未设置</span>
              </template>
              <template v-else-if="column.key === 'action'">
                <a-button type="link" size="small" @click="handleTestModel(record)" :loading="testingModelId === record.id">
                  测试
                </a-button>
                <a-button type="link" size="small" @click="handleEditModel(record)">编辑</a-button>
                <a-popconfirm title="确定删除吗？" @confirm="handleDeleteModel(record.id)">
                  <a-button type="link" size="small" danger>删除</a-button>
                </a-popconfirm>
              </template>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="model-chains" tab="模型链路">
          <a-form :model="configData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }" style="max-width: 860px">
            <a-alert
              type="info"
              show-icon
              message="轻重任务链路独立配置"
              description="轻任务固定走 light 链，重任务固定走 heavy 链。保存时会校验所有槽位是否完整且模型已启用。"
              style="margin-bottom: 16px"
            />

            <a-divider orientation="left">轻任务链路</a-divider>
            <a-form-item label="轻任务主模型" required>
              <a-select
                v-model:value="configData.light_primary_model_id"
                placeholder="请选择轻任务主模型"
                :options="enabledModelOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>
            <a-form-item label="轻任务备选模型" required>
              <a-select
                v-model:value="configData.light_secondary_model_id"
                placeholder="请选择轻任务备选模型"
                :options="enabledModelOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>

            <a-divider orientation="left">重任务链路</a-divider>
            <a-form-item label="重任务主模型" required>
              <a-select
                v-model:value="configData.heavy_primary_model_id"
                placeholder="请选择重任务主模型"
                :options="enabledModelOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>
            <a-form-item label="重任务第一备选" required>
              <a-select
                v-model:value="configData.heavy_secondary_model_id"
                placeholder="请选择重任务第一备选模型"
                :options="enabledModelOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>
            <a-form-item label="重任务第二备选" required>
              <a-select
                v-model:value="configData.heavy_tertiary_model_id"
                placeholder="请选择重任务第二备选模型"
                :options="enabledModelOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>

            <a-form-item :wrapper-col="{ span: 18, offset: 6 }">
              <a-button type="primary" :loading="savingConfig" @click="handleSaveModelChains">
                保存模型链路
              </a-button>
            </a-form-item>
          </a-form>
        </a-tab-pane>
      </a-tabs>
    </a-card>

    <!-- 从 OpenRouter 导入模型弹窗 -->
    <a-modal
      v-model:open="importModalVisible"
      title="从 OpenRouter 导入模型"
      width="900px"
      :confirm-loading="importing"
      @ok="handleConfirmImport"
      @cancel="handleCancelImport"
    >
      <a-input-search
        v-model:value="openRouterSearchKeyword"
        placeholder="搜索模型名称"
        style="margin-bottom: 16px"
        @search="handleSearchOpenRouterModels"
      />
      <a-table
        :columns="openRouterColumns"
        :data-source="openRouterModels"
        :loading="loadingOpenRouterModels"
        :pagination="{ pageSize: 10 }"
        row-key="id"
        :row-selection="{
          selectedRowKeys: selectedOpenRouterModels,
          onChange: onSelectOpenRouterModels
        }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'context_length'">
            {{ record.context_length ? record.context_length.toLocaleString() : '-' }}
          </template>
        </template>
      </a-table>
    </a-modal>

    <!-- 添加/编辑模型弹窗 -->
    <a-modal
      v-model:open="modelModalVisible"
      :title="isEditModel ? '编辑模型' : '添加模型'"
      width="600px"
      @ok="handleSubmitModel"
      @cancel="handleCancelModel"
    >
      <a-form :model="modelFormData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="模型名称" required>
          <a-input
            v-model:value="modelFormData.model_name"
            placeholder="如：gpt-4, gemini-pro@google"
          />
        </a-form-item>
        <a-form-item label="花名">
          <a-input
            v-model:value="modelFormData.alias"
            placeholder="非管理员看到的名称，留空则显示&ldquo;神算子模型#ID&rdquo;"
          />
        </a-form-item>
        <a-form-item label="来源" required>
          <a-select 
            v-model:value="modelFormData.source" 
            placeholder="请选择服务商"
            style="width: 100%"
            :loading="loadingProviders"
            @change="handleModelSourceChange"
          >
            <a-select-option 
              v-for="provider in modelSourceProviders"
              :key="provider.provider_code" 
              :value="provider.provider_code"
              :disabled="provider.isLegacy"
            >
              {{ provider.provider_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="模型ID" required>
          <a-select
            v-if="loadingProviderModels || providerModels.length > 0"
            v-model:value="modelFormData.model_id"
            placeholder="请选择服务商支持的模型"
            show-search
            allow-clear
            :loading="loadingProviderModels"
            :disabled="loadingProviderModels"
            :filter-option="filterProviderModelOption"
            style="width: 100%"
            @change="handleProviderModelChange"
          >
            <a-select-option
              v-for="model in providerModels"
              :key="model.id"
              :value="model.id"
            >
              {{ model.name || model.id }}
              <span v-if="model.description" style="color: #999; font-size: 12px;">
                - {{ model.description }}
              </span>
            </a-select-option>
          </a-select>
          <a-input
            v-else
            v-model:value="modelFormData.model_id"
            :disabled="!modelFormData.source"
            placeholder="用于 API 调用的模型 ID，如：openai/gpt-4"
          />
          <div v-if="modelFormData.source" style="margin-top: 4px; color: #999; font-size: 12px;">
            <span v-if="loadingProviderModels">正在获取服务商支持的模型列表…</span>
            <template v-else-if="providerModelsError">
              <span>{{ providerModelsError }}，请手动输入模型 ID。</span>
              <a-button type="link" size="small" @click="handleRetryProviderModels">重试</a-button>
            </template>
            <span v-else-if="providerModels.length > 0">已加载 {{ providerModels.length }} 个可选模型</span>
          </div>
        </a-form-item>
        <a-form-item v-if="modelFormData.source === 'direct'" label="API地址">
          <a-input
            v-model:value="modelFormData.api_url"
            placeholder="直接调用时的API地址"
          />
        </a-form-item>
        <a-form-item label="是否默认模型">
          <a-switch v-model:checked="modelFormData.is_default" />
        </a-form-item>
        <a-form-item v-if="modelFormData.is_default" label="优先级">
          <a-input-number
            v-model:value="modelFormData.priority"
            :min="0"
            :max="999"
            placeholder="越小越优先"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="是否后台模型">
          <a-switch v-model:checked="modelFormData.is_backend" />
        </a-form-item>
        <a-form-item label="语义匹配专用">
          <a-switch v-model:checked="modelFormData.is_semantic_match" />
          <span style="margin-left: 8px; color: #888; font-size: 12px">用于故障现象语义匹配的专用模型</span>
        </a-form-item>
        <a-form-item label="是否启用">
          <a-switch v-model:checked="modelFormData.is_enabled" />
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea v-model:value="modelFormData.remark" :rows="2" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 响应结果弹窗 -->
    <a-modal
      v-model:open="responseModalVisible"
      title="大模型响应"
      width="900px"
      :footer="null"
    >
      <div style="margin-bottom: 16px">
        <a-button type="primary" @click="handleCopyResponse">
          <template #icon><CopyOutlined /></template>
          复制全部内容
        </a-button>
      </div>
      <div style="background: #f5f5f5; padding: 16px; border-radius: 4px; max-height: 500px; overflow-y: auto">
        <pre style="margin: 0; white-space: pre-wrap; word-wrap: break-word; font-family: 'Courier New', monospace;">{{ responseContent }}</pre>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons-vue';
import {
  getConfig,
  updateConfig,
  testOpenRouterApiKey,
  testDoubaoApiKey,
  testCloudAceApiKey,
  testGoogleAiStudioApiKey,
  testVertexAiApiKey,
  getOpenRouterModels,
  testModel
} from '../api/config';
import {
  getModels,
  createModel,
  updateModel,
  deleteModel,
  batchCreateModels
} from '../api/model';
import { getEnabledProviders, fetchProviderModels } from '../api/provider';
import ProviderManage from './ProviderManage.vue';

export default {
  name: 'Config',
  components: {
    PlusOutlined,
    DownloadOutlined,
    CopyOutlined,
    ProviderManage
  },
  setup() {
    const activeTab = ref('providers');
    const configFormRef = ref(null);
    const configData = reactive({
      openrouter_api_url: 'https://openrouter.ai/api/v1',
      openrouter_api_key: '',
      openrouter_test_model_id: '',
      doubao_api_url: '',
      doubao_api_key: '',
      doubao_test_model_id: '',
      cloudace_api_url: 'https://sgp.mrjc.cc/v1',
      cloudace_api_key: '',
      cloudace_test_model_id: '',
      vertex_ai_project_id: '',
      vertex_ai_test_model_id: '',
      google_ai_studio_api_key: '',
      google_ai_studio_test_model_id: '',
      test_prompt: '',
      judge_model_id: '',
      light_primary_model_id: null,
      light_secondary_model_id: null,
      heavy_primary_model_id: null,
      heavy_secondary_model_id: null,
      heavy_tertiary_model_id: null
    });
    const savingConfig = ref(false);
    const testingOpenRouter = ref(false);
    const testingDoubao = ref(false);
    const testingCloudAce = ref(false);
    const testingVertexAi = ref(false);
    const testingGoogleAiStudio = ref(false);
    const testingModelId = ref(null);

    // 模型列表
    const modelList = ref([]);
    const allEnabledModels = ref([]); // 所有启用的模型（用于选择器）
    const modelLoading = ref(false);
    const modelPagination = ref({
      current: 1,
      pageSize: 50,
      total: 0,
      showSizeChanger: false,
      showTotal: (total) => `共 ${total} 条`
    });
    const modelColumns = [
      { title: '模型名称', dataIndex: 'model_name', key: 'model_name' },
      { title: '花名', dataIndex: 'alias', key: 'alias' },
      { title: '模型ID', dataIndex: 'model_id', key: 'model_id' },
      { title: '来源', key: 'source', width: 120 },
      { title: '默认模型', key: 'is_default', width: 100 },
      { title: '优先级', key: 'priority', width: 100 },
      { title: '后台模型', key: 'is_backend', width: 100 },
      { title: '语义匹配(单选)', key: 'is_semantic_match', width: 110 },
      { title: '启用', key: 'is_enabled', width: 80 },
      { title: '测试状态', key: 'is_tested', width: 100 },
      { title: '操作', key: 'action', width: 200 }
    ];

    // OpenRouter 导入相关
    const importModalVisible = ref(false);
    const openRouterModels = ref([]);
    const loadingOpenRouterModels = ref(false);
    const selectedOpenRouterModels = ref([]);
    const openRouterSearchKeyword = ref('');
    const importing = ref(false);
    const openRouterColumns = [
      { title: '模型ID', dataIndex: 'id', key: 'id' },
      { title: '模型名称', dataIndex: 'name', key: 'name' },
      { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
      { title: '上下文长度', key: 'context_length', width: 120 }
    ];

    // 模型编辑相关
    const modelModalVisible = ref(false);
    const isEditModel = ref(false);
    const modelFormData = reactive({
      model_name: '',
      model_id: '',
      source: '',
      api_url: '',
      is_default: false,
      priority: 0,
      is_backend: false,
      is_semantic_match: false,
      is_enabled: true,
      remark: '',
      alias: ''
    });

    // 服务商列表（用于模型来源选择）
    const enabledProviders = ref([]);
    const loadingProviders = ref(false);
    const providerModels = ref([]);
    const loadingProviderModels = ref(false);
    const providerModelsError = ref('');
    let providerModelsRequestId = 0;

    // 新建模型只允许选择可调用的服务商；编辑历史模型时保留旧来源，避免无法保存旧数据。
    const modelSourceProviders = computed(() => {
      const providers = [...enabledProviders.value];
      if (
        isEditModel.value &&
        modelFormData.source &&
        !providers.some(provider => provider.provider_code === modelFormData.source)
      ) {
        providers.unshift({
          provider_code: modelFormData.source,
          provider_name: `${getSourceDisplayName(modelFormData.source)}（历史或已禁用）`,
          isLegacy: true
        });
      }
      return providers;
    });

    // 加载服务商列表
    const loadProviders = async () => {
      loadingProviders.value = true;
      try {
        const res = await getEnabledProviders();
        if (res.code === 200) {
          enabledProviders.value = res.data || [];
        }
      } catch (error) {
        console.error('加载服务商列表失败:', error);
      } finally {
        loadingProviders.value = false;
      }
    };

    const resetProviderModels = () => {
      providerModelsRequestId += 1;
      providerModels.value = [];
      providerModelsError.value = '';
      loadingProviderModels.value = false;
    };

    const loadProviderModels = async (providerCode, { clearModelId = true } = {}) => {
      const requestId = ++providerModelsRequestId;
      providerModels.value = [];
      providerModelsError.value = '';

      if (clearModelId) {
        modelFormData.model_id = '';
      }

      const provider = enabledProviders.value.find(item => item.provider_code === providerCode);
      if (!provider) {
        providerModelsError.value = '当前来源不是已启用的服务商';
        return;
      }

      loadingProviderModels.value = true;
      try {
        const res = await fetchProviderModels(provider.id);
        if (requestId !== providerModelsRequestId) {
          return;
        }

        const models = res.code === 200 ? [...(res.data?.models || [])] : [];
        if (
          !clearModelId &&
          modelFormData.model_id &&
          !models.some(model => model.id === modelFormData.model_id)
        ) {
          models.unshift({
            id: modelFormData.model_id,
            name: modelFormData.model_name || modelFormData.model_id,
            description: '当前已保存的模型'
          });
        }
        providerModels.value = models;
        if (models.length === 0) {
          providerModelsError.value = res.message || '该服务商未返回可用模型列表';
        }
      } catch (error) {
        if (requestId !== providerModelsRequestId) {
          return;
        }
        providerModelsError.value =
          error.response?.data?.message || error.message || '获取模型列表失败';
      } finally {
        if (requestId === providerModelsRequestId) {
          loadingProviderModels.value = false;
        }
      }
    };

    const handleModelSourceChange = async (providerCode) => {
      modelFormData.api_url = '';
      await loadProviderModels(providerCode);
    };

    const handleRetryProviderModels = async () => {
      await loadProviderModels(modelFormData.source, { clearModelId: false });
    };

    const handleProviderModelChange = (modelId) => {
      if (!modelId || modelFormData.model_name) {
        return;
      }
      const selectedModel = providerModels.value.find(model => model.id === modelId);
      if (selectedModel) {
        modelFormData.model_name = selectedModel.name || selectedModel.id;
      }
    };

    const filterProviderModelOption = (input, option) => {
      const model = providerModels.value.find(item => item.id === option.value);
      if (!model) {
        return false;
      }
      const keyword = input.toLowerCase();
      return (
        String(model.id).toLowerCase().includes(keyword) ||
        String(model.name || '').toLowerCase().includes(keyword) ||
        String(model.description || '').toLowerCase().includes(keyword)
      );
    };

    // 加载配置
    const loadConfig = async () => {
      try {
        const res = await getConfig();
        if (res.data) {
          Object.assign(configData, res.data);
        }
      } catch (error) {
        console.error('加载配置失败:', error);
      }
    };

    // 保存配置
    const handleSaveConfig = async () => {
      savingConfig.value = true;
      try {
        await updateConfig(configData);
        message.success('配置保存成功');
      } catch (error) {
        message.error('配置保存失败');
      } finally {
        savingConfig.value = false;
      }
    };

    const validateModelChainConfig = () => {
      const requiredFields = [
        { key: 'light_primary_model_id', label: '轻任务主模型' },
        { key: 'light_secondary_model_id', label: '轻任务备选模型' },
        { key: 'heavy_primary_model_id', label: '重任务主模型' },
        { key: 'heavy_secondary_model_id', label: '重任务第一备选模型' },
        { key: 'heavy_tertiary_model_id', label: '重任务第二备选模型' }
      ];

      const missingField = requiredFields.find((field) => !configData[field.key]);
      if (missingField) {
        message.warning(`请先选择${missingField.label}`);
        return false;
      }
      return true;
    };

    const handleSaveModelChains = async () => {
      if (!validateModelChainConfig()) {
        return;
      }
      await handleSaveConfig();
    };

    // 测试 OpenRouter API Key
    const handleTestOpenRouter = async () => {
      if (!configData.openrouter_api_url || !configData.openrouter_api_key) {
        message.warning('请先填写 API URL 和 API Key');
        return;
      }
      // 如果有测试模型ID，自动使用测试提示词
      const useTestPrompt = !!configData.openrouter_test_model_id;
      if (useTestPrompt && !configData.test_prompt) {
        message.warning('请先保存测试提示词');
        return;
      }
      testingOpenRouter.value = true;
      try {
        const res = await testOpenRouterApiKey(
          configData.openrouter_api_url,
          configData.openrouter_api_key,
          configData.openrouter_test_model_id || null,
          useTestPrompt
        );
        if (res.code === 200) {
          if (useTestPrompt && res.data?.response) {
            // 使用测试提示词，显示响应结果
            responseContent.value = res.data.response;
            responseModalVisible.value = true;
          } else {
            message.success('API Key 有效');
          }
        } else {
          const errorMsg = res.message || res.data?.message || 'API Key 无效';
          console.error('OpenRouter API 测试失败:', res);
          message.error(errorMsg);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        console.error('OpenRouter API 测试异常:', error);
        console.error('错误响应:', error.response?.data);
        message.error('测试失败：' + errorMsg);
      } finally {
        testingOpenRouter.value = false;
      }
    };

    // 测试豆包 API Key
    const handleTestDoubao = async () => {
      if (!configData.doubao_api_url || !configData.doubao_api_key) {
        message.warning('请先填写 API URL 和 API Key');
        return;
      }
      if (!configData.doubao_test_model_id) {
        message.warning('请填写测试模型ID');
        return;
      }
      // 自动使用测试提示词
      if (!configData.test_prompt) {
        message.warning('请先保存测试提示词');
        return;
      }
      testingDoubao.value = true;
      try {
        const res = await testDoubaoApiKey(
          configData.doubao_api_url,
          configData.doubao_api_key,
          configData.doubao_test_model_id,
          true
        );
        if (res.code === 200) {
          if (res.data?.response) {
            // 使用测试提示词，显示响应结果
            responseContent.value = res.data.response;
            responseModalVisible.value = true;
          } else {
            message.success('API Key 有效');
          }
        } else {
          const errorMsg = res.message || res.data?.message || 'API Key 无效';
          console.error('豆包 API 测试失败:', res);
          message.error(errorMsg);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        console.error('豆包 API 测试异常:', error);
        console.error('错误响应:', error.response?.data);
        message.error('测试失败：' + errorMsg);
      } finally {
        testingDoubao.value = false;
      }
    };

    // 测试 CloudAce API Key
    const handleTestCloudAce = async () => {
      if (!configData.cloudace_api_url || !configData.cloudace_api_key) {
        message.warning('请先填写 API URL 和 API Key');
        return;
      }
      if (!configData.cloudace_test_model_id) {
        message.warning('请填写测试模型ID');
        return;
      }
      // 自动使用测试提示词
      if (!configData.test_prompt) {
        message.warning('请先保存测试提示词');
        return;
      }
      testingCloudAce.value = true;
      try {
        const res = await testCloudAceApiKey(
          configData.cloudace_api_url,
          configData.cloudace_api_key,
          configData.cloudace_test_model_id,
          true
        );
        if (res.code === 200) {
          if (res.data?.response) {
            // 使用测试提示词，显示响应结果
            responseContent.value = res.data.response;
            responseModalVisible.value = true;
          } else {
            message.success('API Key 有效');
          }
        } else {
          const errorMsg = res.message || res.data?.message || 'API Key 无效';
          console.error('CloudAce API 测试失败:', res);
          message.error(errorMsg);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        console.error('CloudAce API 测试异常:', error);
        console.error('错误响应:', error.response?.data);
        message.error('测试失败：' + errorMsg);
      } finally {
        testingCloudAce.value = false;
      }
    };

    // 测试 Vertex AI API Key
    const handleTestVertexAi = async () => {
      if (!configData.vertex_ai_project_id) {
        message.warning('请先填写项目ID');
        return;
      }
      if (!configData.vertex_ai_test_model_id) {
        message.warning('请填写测试模型ID');
        return;
      }
      // 自动使用测试提示词
      if (!configData.test_prompt) {
        message.warning('请先保存测试提示词');
        return;
      }
      testingVertexAi.value = true;
      try {
        const res = await testVertexAiApiKey(
          null, // API Key 不再需要，使用服务账号认证
          configData.vertex_ai_test_model_id,
            configData.vertex_ai_project_id || null,
            'us-central1',
            null,
          true
        );
        if (res.code === 200) {
          if (res.data?.response) {
            // 使用测试提示词，显示响应结果
            responseContent.value = res.data.response;
            responseModalVisible.value = true;
          } else {
            message.success('API Key 有效');
          }
        } else {
          const errorMsg = res.message || res.data?.message || 'API Key 无效';
          console.error('Vertex AI API 测试失败:', res);
          message.error(errorMsg);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        console.error('Vertex AI API 测试异常:', error);
        console.error('错误响应:', error.response?.data);
        message.error('测试失败：' + errorMsg);
      } finally {
        testingVertexAi.value = false;
      }
    };

    // 测试 Google AI Studio API Key
    const handleTestGoogleAiStudio = async () => {
      if (!configData.google_ai_studio_api_key) {
        message.warning('请先填写 API Key');
        return;
      }
      if (!configData.google_ai_studio_test_model_id) {
        message.warning('请填写测试模型ID');
        return;
      }
      // 自动使用测试提示词
      if (!configData.test_prompt) {
        message.warning('请先保存测试提示词');
        return;
      }
      testingGoogleAiStudio.value = true;
      try {
        const res = await testGoogleAiStudioApiKey(
          configData.google_ai_studio_api_key,
          configData.google_ai_studio_test_model_id,
          true
        );
        if (res.code === 200) {
          if (res.data?.response) {
            // 使用测试提示词，显示响应结果
            responseContent.value = res.data.response;
            responseModalVisible.value = true;
          } else {
            message.success('API Key 有效');
          }
        } else {
          const errorMsg = res.message || res.data?.message || 'API Key 无效';
          console.error('Google AI Studio API 测试失败:', res);
          message.error(errorMsg);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        console.error('Google AI Studio API 测试异常:', error);
        console.error('错误响应:', error.response?.data);
        message.error('测试失败：' + errorMsg);
      } finally {
        testingGoogleAiStudio.value = false;
      }
    };

    // 加载模型列表
    const loadModels = async (page = 1) => {
      modelLoading.value = true;
      try {
        const res = await getModels({
          page,
          pageSize: modelPagination.value.pageSize
        });
        if (res.data) {
          modelList.value = res.data.list || [];
          modelPagination.value.total = res.data.total || 0;
          modelPagination.value.current = res.data.page || 1;
        }
      } catch (error) {
        message.error('加载模型列表失败');
      } finally {
        modelLoading.value = false;
      }
    };

    const loadEnabledModelOptions = async () => {
      try {
        const res = await getModels({ page: 1, pageSize: 500 });
        if (res.data) {
          const models = res.data.list || [];
          allEnabledModels.value = models.filter((model) => model.is_enabled === 1);
        }
      } catch (error) {
        console.error('加载模型选项失败:', error);
      }
    };

    // 表格变化处理（分页）
    const handleTableChange = (pagination) => {
      modelPagination.value.current = pagination.current;
      loadModels(pagination.current);
    };

    // 测试模型
    const handleTestModel = async (model) => {
      testingModelId.value = model.id;
      try {
        const res = await testModel(model.id);
        if (res.code === 200) {
          // 显示响应结果
          responseContent.value = typeof res.data === 'string' 
            ? res.data 
            : JSON.stringify(res.data, null, 2);
          responseModalVisible.value = true;
          // 测试成功，刷新列表以更新测试状态
          await loadModels(modelPagination.value.current);
          message.success('测试成功');
        } else {
          message.error(res.message || '测试失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        message.error('测试失败：' + errorMsg);
      } finally {
        testingModelId.value = null;
      }
    };

    // 从 OpenRouter 导入
    const handleImportFromOpenRouter = async () => {
      importModalVisible.value = true;
      selectedOpenRouterModels.value = [];
      await handleSearchOpenRouterModels();
    };

    // 搜索 OpenRouter 模型
    const handleSearchOpenRouterModels = async () => {
      loadingOpenRouterModels.value = true;
      try {
        const res = await getOpenRouterModels(
          configData.openrouter_api_url,
          configData.openrouter_api_key
        );
        if (res.data) {
          let models = res.data;
          // 如果有搜索关键词，进行过滤
          if (openRouterSearchKeyword.value) {
            const keyword = openRouterSearchKeyword.value.toLowerCase();
            models = models.filter(
              m => m.id.toLowerCase().includes(keyword) || 
                   (m.name && m.name.toLowerCase().includes(keyword))
            );
          }
          openRouterModels.value = models;
        }
      } catch (error) {
        message.error('获取模型列表失败：' + (error.response?.data?.message || error.message));
      } finally {
        loadingOpenRouterModels.value = false;
      }
    };

    // 选择 OpenRouter 模型
    const onSelectOpenRouterModels = (selectedKeys) => {
      selectedOpenRouterModels.value = selectedKeys;
    };

    // 确认导入
    const handleConfirmImport = async () => {
      if (selectedOpenRouterModels.value.length === 0) {
        message.warning('请至少选择一个模型');
        return;
      }
      importing.value = true;
      try {
        const selectedModels = openRouterModels.value.filter(
          m => selectedOpenRouterModels.value.includes(m.id)
        );
        const modelsToImport = selectedModels.map(m => ({
          model_name: m.id,
          model_id: m.id,
          source: 'openrouter',
          is_default: 0,
          is_backend: 0,
          priority: 0,
          is_enabled: 1,
          remark: m.description || ''
        }));
        await batchCreateModels(modelsToImport);
        message.success(`成功导入 ${modelsToImport.length} 个模型`);
        importModalVisible.value = false;
        await loadModels();
      } catch (error) {
        message.error('导入失败：' + (error.response?.data?.message || error.message));
      } finally {
        importing.value = false;
      }
    };

    // 取消导入
    const handleCancelImport = () => {
      importModalVisible.value = false;
      selectedOpenRouterModels.value = [];
      openRouterSearchKeyword.value = '';
    };

    // 添加模型
    const handleAddModel = () => {
      isEditModel.value = false;
      resetProviderModels();
      Object.assign(modelFormData, {
        model_name: '',
        model_id: '',
        source: '',
        api_url: '',
        is_default: false,
        priority: 0,
        is_backend: false,
        is_semantic_match: false,
        is_enabled: true,
        remark: '',
        alias: ''
      });
      modelModalVisible.value = true;
    };

    // 编辑模型
    const handleEditModel = async (record) => {
      isEditModel.value = true;
      resetProviderModels();
      Object.assign(modelFormData, {
        model_name: record.model_name,
        model_id: record.model_id,
        source: record.source,
        api_url: record.api_url || '',
        is_default: record.is_default === 1,
        priority: record.priority || 0,
        is_backend: record.is_backend === 1,
        is_semantic_match: record.is_semantic_match === 1,
        is_enabled: record.is_enabled === 1,
        remark: record.remark || '',
        alias: record.alias || ''
      });
      modelFormData._id = record.id;
      modelModalVisible.value = true;
      await loadProviderModels(record.source, { clearModelId: false });
    };

    // 提交模型
    const handleSubmitModel = async () => {
      try {
        if (!modelFormData.source) {
          message.warning('请选择服务商');
          return;
        }
        if (!modelFormData.model_id) {
          message.warning('请选择或填写模型 ID');
          return;
        }
        if (!modelFormData.model_name) {
          message.warning('请填写模型名称');
          return;
        }
        const data = {
          ...modelFormData,
          is_default: modelFormData.is_default ? 1 : 0,
          is_backend: modelFormData.is_backend ? 1 : 0,
          is_semantic_match: modelFormData.is_semantic_match ? 1 : 0,
          is_enabled: modelFormData.is_enabled ? 1 : 0
        };
        if (isEditModel.value) {
          await updateModel(modelFormData._id, data);
          message.success('更新成功');
        } else {
          await createModel(data);
          message.success('添加成功');
        }
        modelModalVisible.value = false;
        await loadModels();
      } catch (error) {
        message.error((isEditModel.value ? '更新' : '添加') + '失败：' + (error.response?.data?.message || error.message));
      }
    };

    // 取消模型编辑
    const handleCancelModel = () => {
      resetProviderModels();
      modelModalVisible.value = false;
    };

    // 切换默认模型
    const handleToggleDefault = async (record, checked) => {
      try {
        await updateModel(record.id, {
          is_default: checked ? 1 : 0,
          priority: checked ? (record.priority || 0) : 0
        });
        await loadModels();
      } catch (error) {
        message.error('更新失败');
      }
    };

    // 切换后台模型
    const handleToggleBackend = async (record, checked) => {
      try {
        await updateModel(record.id, { is_backend: checked ? 1 : 0 });
        await loadModels();
      } catch (error) {
        message.error('更新失败');
      }
    };

    // 切换语义匹配专用模型（单选，后端保证互斥）
    const handleToggleSemanticMatch = async (record) => {
      try {
        await updateModel(record.id, { is_semantic_match: 1 });
        await loadModels();
      } catch (error) {
        message.error('更新失败');
      }
    };

    // 更新优先级
    const handleUpdatePriority = async (record) => {
      try {
        await updateModel(record.id, { priority: record.priority || 0 });
      } catch (error) {
        message.error('更新优先级失败');
        await loadModels();
      }
    };

    // 切换启用状态
    const handleToggleEnabled = async (record, checked) => {
      try {
        await updateModel(record.id, { is_enabled: checked ? 1 : 0 });
        await loadModels();
      } catch (error) {
        message.error('更新失败');
      }
    };

    // 删除模型
    const handleDeleteModel = async (id) => {
      try {
        await deleteModel(id);
        message.success('删除成功');
        await loadModels();
      } catch (error) {
        message.error('删除失败：' + (error.response?.data?.message || error.message));
      }
    };

    // 测试提示词相关
    const responseModalVisible = ref(false);
    const responseContent = ref('');

    // 复制响应内容
    const handleCopyResponse = async () => {
      try {
        await navigator.clipboard.writeText(responseContent.value);
        message.success('已复制到剪贴板');
      } catch (error) {
        // 降级方案
        const textArea = document.createElement('textarea');
        textArea.value = responseContent.value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          message.success('已复制到剪贴板');
        } catch (err) {
          message.error('复制失败');
        }
        document.body.removeChild(textArea);
      }
    };

    // 获取来源名称（优先从服务商列表获取）
    const getSourceName = (source) => {
      return getSourceDisplayName(source);
    };

    const getSourceDisplayName = (source) => {
      const provider = enabledProviders.value.find(p => p.provider_code === source);
      if (provider) {
        return provider.provider_name;
      }
      // 兼容旧的硬编码映射
      const map = {
        'openrouter': 'OpenRouter',
        'doubao': '豆包',
        'cloudace': 'CloudAce AI',
        'vertex_ai': 'Vertex AI',
        'google_ai_studio': 'Google AI Studio',
        'deepseek': 'DeepSeek',
        'direct': '直接调用'
      };
      return map[source] || source;
    };

    // 获取来源颜色
    const getSourceColor = (source) => {
      const map = {
        'openrouter': 'blue',
        'doubao': 'orange',
        'cloudace': 'green',
        'vertex_ai': 'cyan',
        'google_ai_studio': 'geekblue',
        'deepseek': 'purple',
        'direct': 'default'
      };
      return map[source] || 'default';
    };

    // 计算启用的模型列表（用于评分模型选择器）
    const enabledModels = computed(() => {
      // 优先使用 allEnabledModels，如果为空则使用当前页的模型
      return allEnabledModels.value.length > 0 
        ? allEnabledModels.value 
        : modelList.value.filter(m => m.is_enabled === 1);
    });

    const enabledModelOptions = computed(() => enabledModels.value.map((model) => ({
      value: model.id,
      label: `${model.model_name} (${model.model_id})`
    })));

    // 模型选择器过滤函数
    const filterModelOption = (input, option) => {
      const model = enabledModels.value.find(m => m.id === option.value);
      if (!model) return false;
      const searchText = input.toLowerCase();
      return (
        model.model_name.toLowerCase().includes(searchText) ||
        model.model_id.toLowerCase().includes(searchText)
      );
    };

    onMounted(async () => {
      await loadConfig();
      await loadModels();
      await loadEnabledModelOptions();
      await loadProviders();
    });

    return {
      activeTab,
      configFormRef,
      configData,
      savingConfig,
      testingOpenRouter,
      testingDoubao,
      testingCloudAce,
      testingModelId,
      testingVertexAi,
      testingGoogleAiStudio,
      handleSaveConfig,
      handleSaveModelChains,
      handleTestOpenRouter,
      handleTestDoubao,
      handleTestCloudAce,
      handleTestVertexAi,
      handleTestGoogleAiStudio,
      modelList,
      modelLoading,
      modelColumns,
      loadModels,
      handleImportFromOpenRouter,
      importModalVisible,
      openRouterModels,
      loadingOpenRouterModels,
      selectedOpenRouterModels,
      openRouterSearchKeyword,
      importing,
      openRouterColumns,
      handleSearchOpenRouterModels,
      onSelectOpenRouterModels,
      handleConfirmImport,
      handleCancelImport,
      handleAddModel,
      handleEditModel,
      modelModalVisible,
      isEditModel,
      modelFormData,
      handleSubmitModel,
      handleCancelModel,
      handleToggleDefault,
      handleToggleBackend,
      handleToggleSemanticMatch,
      handleUpdatePriority,
      handleToggleEnabled,
      handleDeleteModel,
      handleTestModel,
      getSourceName,
      getSourceColor,
      responseModalVisible,
      responseContent,
      handleCopyResponse,
      enabledModels,
      enabledModelOptions,
      filterModelOption,
      modelPagination,
      handleTableChange,
      enabledProviders,
      loadingProviders,
      modelSourceProviders,
      providerModels,
      loadingProviderModels,
      providerModelsError,
      handleModelSourceChange,
      handleRetryProviderModels,
      handleProviderModelChange,
      filterProviderModelOption
    };
  }
};
</script>

<style scoped>
</style>
